/*----------------------------------
- HOOK: LAZY PREVIEW DRAWER
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @docs docs/features/auctions
 * @adr  ADR-0028
 * @rule A public preview drawer's chunk is requested only once a row is opened: it carries
 *       framer-motion, so a static import would put it in the page's eager chunk. A failed chunk is
 *       forgotten and the row cleared with reason `error`, so the next row click requests it again.
 *       One loader and one hook for every public preview drawer (`/domains*`, Home, `/auctions`);
 *       each lazy drawer only names its chunk and the prop its subject travels in.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

import { appErrorReporter } from '@/common/libs/errorReporting';

// Types
import type { PublicPreviewDrawerCloseReason } from './PublicPreviewDrawerFrame';

/*----------------------------------
- TYPES
----------------------------------*/

export type PreviewDrawerComponent<TProps> = (props: TProps) => React.JSX.Element | null;

export type PreviewDrawerLoader<TProps> = {
    /** One request per page visit, shared by every caller. */
    load: () => Promise<PreviewDrawerComponent<TProps>>;
    /** Drops a failed request, so the next open asks for the chunk again. */
    forget: () => void;
};

/*----------------------------------
- LOADER
----------------------------------*/

/** The drawer chunk behind a dynamic `import()`, requested at most once at a time. */
export const createPreviewDrawerLoader = <TProps>(importDrawer: () => Promise<{ default: PreviewDrawerComponent<TProps> }>): PreviewDrawerLoader<TProps> => {
    let drawerModule: Promise<PreviewDrawerComponent<TProps>> | null = null;
    return {
        load: () => {
            drawerModule ??= importDrawer().then((module) => module.default);
            return drawerModule;
        },
        forget: () => {
            drawerModule = null;
        },
    };
};

/*----------------------------------
- HOOK
----------------------------------*/

/**
 * The loaded drawer, the subject to draw and whether it is open. The drawer keeps drawing the
 * subject it closed on while its exit plays, as the Product's does.
 */
export const useLazyPreviewDrawer = <TSubject, TProps>(
    loader: PreviewDrawerLoader<TProps>,
    subject: TSubject | null,
    onRequestClose: (reason: PublicPreviewDrawerCloseReason) => void,
) => {
    const [Drawer, setDrawer] = React.useState<PreviewDrawerComponent<TProps> | null>(null);
    const [lastSubject, setLastSubject] = React.useState<TSubject | null>(subject);
    const isOpen = subject !== null;

    React.useEffect(() => {
        if (subject !== null) setLastSubject(subject);
    }, [subject]);

    React.useEffect(() => {
        if (!isOpen || Drawer) return;

        let disposed = false;
        loader.load()
            .then((component) => {
                if (!disposed) setDrawer(() => component);
            })
            .catch((error) => {
                // A failed chunk is forgotten and the preview cleared, reason `error`: the dismiss
                // event records the failure, and since the subject goes back to null the next row
                // click reopens the drawer and requests the chunk again.
                loader.forget();
                if (!disposed) onRequestClose('error');
                appErrorReporter.app.handleError(error);
            });
        return () => {
            disposed = true;
        };
    }, [Drawer, isOpen, loader, onRequestClose]);

    return { Drawer, drawn: subject ?? lastSubject, isOpen };
};
